import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  styled,
  Typography,
  Skeleton,
} from '@mui/material';
import { DateTime } from 'luxon';

import type {
  SyncedTripLeg,
  TripLeg,
} from '../../../../data/models/reservation.model';

import { useAppSelector } from '../../../../hooks/useAppSelector';

import { selectTimeZones } from '../../../../data/lookup.slice';
import {
  selectEstimatedTripLegPricing,
  selectLoadedEstimatedTripLegPricing,
  selectLoadingEstimatedTripLegPricing,
} from '../../../../data/accounting.slice';

import dateUtils from '../../../../services/DateUtils';
import { financialUtils } from '../../../../services/financialUtils';
import tripLegUtils from '../../../../services/triplegUtils';

import TabPanel from '../../../TabPanel';

const PREFIX = 'ProviderCallSummary';
const classes = {
  container: `${PREFIX}-container`,
  currentTripLeg: `${PREFIX}-currentTripLeg`,
};

const StyledScriptBox = styled(Box)(({ theme }) => ({
  whiteSpace: 'pre-line',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: 528,
  padding: theme.spacing(4),

  [`& .${classes.currentTripLeg}`]: {
    color: theme.palette.error.main,
  },
}));

const StyledSummaryBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginTop: theme.spacing(2),

  '& .MuiTypography-root': {
    paddingBottom: 0,
  },
}));

const RandomSkeleton = ({ width }: { width: number }) => (
  <Skeleton variant="text" width={width} height={24} />
);

const SkeletonScript = () => {
  const skeletonWidths = useMemo(
    () =>
      Array.from({ length: 12 }, () => Math.floor(Math.random() * 300) + 100),
    [],
  );

  return (
    <Box>
      {skeletonWidths.slice(0, 4).map((width, index) => (
        <RandomSkeleton key={`skeleton-${index}-${width}`} width={width} />
      ))}
      <ul>
        {skeletonWidths.slice(4, 8).map((width, index) => (
          <li key={`skeleton-${index + 4}-${width}`}>
            <RandomSkeleton width={width} />
          </li>
        ))}
      </ul>
      {skeletonWidths.slice(8).map((width, index) => (
        <RandomSkeleton key={`skeleton-${index + 8}-${width}`} width={width} />
      ))}
    </Box>
  );
};

const ProviderCallSummaryDetails = () => {
  return (
    <Box>
      <Typography>Details</Typography>
    </Box>
  );
};

interface ProviderCallSummaryProps {
  tripLeg: SyncedTripLeg;
  tripLegsInReservation: TripLeg[];
  providerContactName: string;
  isLoading: boolean;
}

const ProviderCallSummary = ({
  tripLeg,
  tripLegsInReservation,
  providerContactName,
  isLoading,
}: ProviderCallSummaryProps) => {
  const { t } = useTranslation('common');
  const [tabIndex, setTabIndex] = useState(1);

  const estimatedTripLegPricing = useAppSelector(selectEstimatedTripLegPricing);
  const loadingEstimatedTripLegPricing = useAppSelector(
    selectLoadingEstimatedTripLegPricing,
  );
  const loadedEstimatedTripLegPricing = useAppSelector(
    selectLoadedEstimatedTripLegPricing,
  );

  // Narrative info
  const timezones = useAppSelector(selectTimeZones);

  let pickupTime = '';
  const tz = timezones.find(
    (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
  );
  const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
    zone: tz?.ianaStandard,
  });
  const puTime = m.toFormat('h:mm a');
  pickupTime = puTime;
  const timeRemaining = m.toRelative({
    style: 'long',
    round: true,
  });

  const callScript = `<p>Hi, this is [First Name] from American Logistics. Am I speaking with <strong>${providerContactName}</strong>?</p>
  I'm calling regarding a trip we need assistance with: <strong>${tripLeg.tripLegReferenceID}</strong>, scheduled for ${pickupTime} in ${timeRemaining}.

  This trip is part of a larger <strong>${tripLeg.reservationReferenceID}</strong>. The total cost of the reservation is <strong>[Total Reservation Cost]</strong>, and this leg is estimated at <strong>[Trip Leg Cost]</strong>.
  We currently don't have a provider assigned, and we'd like to see if you can take this trip.
  <ul><li>Pick-Up Location: <strong>${tripLeg.pickUpAddressLine1} ${tripLeg.pickUpAddressLine2} ${tripLeg.pickUpCity}, ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode}</strong></li>
    <li>Drop-Off Location: <strong>${tripLeg.dropOffAddressLine1} ${tripLeg.dropOffAddressLine2} ${tripLeg.dropOffCity}, ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode}</strong></li>
    <li>Member Name: <strong>${tripLeg.passenger?.passengerFirstName} ${tripLeg.passenger?.passengerLastName}</strong></li>
    <li>Special Instructions: <strong>[Special Instructions]</strong></li></ul>
  <p><strong>Are you able to take this trip? Or entire reservation?</strong></p>
  <strong>[if yes]</strong>
  Okay great! Please ensure the driver is aware of the pick-up time and uses the app to keep the status updated.

  Thanks and have a great day.
  `;

  // Details info
  const totalDistance = tripLegUtils.formatDistance({
    distance: tripLegsInReservation.reduce(
      (acc, leg) => acc + Number(leg?.estimatedDistance ?? 0),
      0,
    ),
    unitPlural: t('findProvider.miles'),
    unitSingular: t('findProvider.mile'),
  });

  const totalPrice = tripLegsInReservation.reduce(
    (acc, leg) =>
      acc + Number(estimatedTripLegPricing[leg.tripLegReferenceID ?? ''] ?? 0),
    0,
  );

  const handleTabChange = (
    _evt: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setTabIndex(newValue);
  };

  return (
    <StyledPaper elevation={2} className={classes.container}>
      <Tabs
        indicatorColor="primary"
        textColor="primary"
        value={tabIndex}
        onChange={handleTabChange}
        variant="fullWidth">
        <Tab sx={{ fontWeight: 600 }} label={t('findProvider.script')} />
        <Tab sx={{ fontWeight: 600 }} label={t('findProvider.details')} />
      </Tabs>
      <TabPanel value={tabIndex} index={0}>
        {isLoading && <SkeletonScript />}
        {!isLoading && (
          <>
            {/* <StyledScriptBox
              dangerouslySetInnerHTML={{
                __html: callScript || '',
              }}
            /> */}
          </>
        )}
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="h3">{t('findProvider.summary')}</Typography>
        <StyledSummaryBox>
          <Typography>
            {t('findProvider.memberName')}:{' '}
            <Typography component="span" variant="body1" className="bold">
              {tripLeg.passengerFirstName} {tripLeg.passengerLastName}
            </Typography>
          </Typography>

          <Typography>
            {t('findProvider.totalDistance')}:{' '}
            <Typography component="span" variant="body1" className="bold">
              {`${totalDistance}`}
            </Typography>
          </Typography>

          <Typography>
            {`${t('findProvider.totalPrice')}: `}
            <Typography component="span" variant="body1" className="bold">
              {financialUtils.formatPrice(totalPrice)}
            </Typography>
          </Typography>
        </StyledSummaryBox>

        {tripLegsInReservation.map((tripLegInReservation, idx) => {
          const classNames = [];
          const currentTripLeg =
            tripLegInReservation.tripLegReferenceID ===
            tripLeg.tripLegReferenceID;
          if (currentTripLeg) {
            classNames.push(classes.currentTripLeg);
          }
          const price = financialUtils.formatPrice(
            Number(tripLegInReservation.payableAmount ?? 0),
          );
          const distance = tripLegUtils.formatDistance({
            distance: tripLegInReservation.estimatedDistance ?? 0,
            unitPlural: t('findProvider.miles'),
            unitSingular: t('findProvider.mile'),
          });
          const label = `${t('findProvider.tripLeg')} ${idx + 1} (${currentTripLeg ? 'Current - ' : ''}${tripLegInReservation.tripLegReferenceID})`;
          return (
            <StyledSummaryBox
              key={`trip-leg-${tripLegInReservation.tripLegReferenceID}`}>
              <Typography variant="subtitle1" className={classNames.join(' ')}>
                {label}
              </Typography>

              <Typography>
                {t('findProvider.pickupTime')}:{' '}
                <Typography
                  component="span"
                  variant="body1"
                  className={`bold ${classNames.join(' ')}`}>
                  {tripLegInReservation.isWillCall
                    ? t('findProvider.willCall')
                    : dateUtils.formatPickupTime(
                        tripLegInReservation.pickUpDueDateTime,
                        tripLegInReservation.pickUpAddress.timeZone!,
                      )}
                </Typography>
              </Typography>

              <Typography>
                {t('findProvider.pickupLocation')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {`${tripLegInReservation.pickUpAddress.addressLine1}, ${tripLegInReservation.pickUpAddress.city}, ${tripLegInReservation.pickUpAddress.stateCode}, ${tripLegInReservation.pickUpAddress.zipCode}`}
                </Typography>
              </Typography>

              <Typography>
                {t('findProvider.dropoffLocation')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {`${tripLegInReservation.dropOffAddress.addressLine1}, ${tripLegInReservation.dropOffAddress.city}, ${tripLegInReservation.dropOffAddress.stateCode}, ${tripLegInReservation.dropOffAddress.zipCode}`}
                </Typography>
              </Typography>

              <Typography>
                {t('findProvider.distance')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {distance}
                </Typography>
              </Typography>

              {loadingEstimatedTripLegPricing && (
                <Skeleton variant="text" width={100} height={24} />
              )}

              {loadedEstimatedTripLegPricing && (
                <Typography>
                  {t('findProvider.price')}:{' '}
                  <Typography component="span" variant="body1" className="bold">
                    {financialUtils.formatPrice(
                      Number(
                        estimatedTripLegPricing[
                          tripLegInReservation.tripLegReferenceID ?? ''
                        ] ?? 0,
                      ),
                    )}
                  </Typography>
                </Typography>
              )}
            </StyledSummaryBox>
          );
        })}
      </TabPanel>
    </StyledPaper>
  );
};

export { ProviderCallSummary };
